import {
  filter,
  switchMap,
  mergeMap,
  catchError,
  tap,
  ignoreElements,
  concatMap,
} from 'rxjs/operators'
import {of, concat, from} from 'rxjs'
import {Epic} from 'redux-observable'
import {
  FullStory,
  isInitialized as isFullStoryInitialized,
} from '@fullstory/browser'
import {RootState, AppActions} from 'app/rootReducer'
import {AuthService} from 'services/api'
import {InstallerType, UserRoles} from 'interfaces'
import {userActions} from './userSlice'
import Toast from 'components/Basic/Toast'
import {store} from 'app/store'
import {history} from 'utils'
import {getInstallerLabel} from './utils/getInstallerLabel'
import {i18n} from 'i18n'

const {t} = i18n
const logoutEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.logout.match),
    switchMap(() =>
      from(AuthService.logout()).pipe(
        mergeMap(() => []),
        catchError((message: string) =>
          concat(of(userActions.logoutFailure(message))),
        ),
      ),
    ),
  )

const logoutFailureEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.logoutFailure.match),
    tap(() => {
      store.dispatch({type: 'reset/store'})
    }),
    ignoreElements(),
  )

const getAccountsListEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.getAccountsList.match),
    switchMap(() =>
      from(AuthService.getAccountList()).pipe(
        mergeMap(({data}) => [userActions.getAccountsListSuccess(data)]),
        catchError((message: string) =>
          concat(of(userActions.getAccountsListFailure(message))),
        ),
      ),
    ),
  )

const getAccountsListFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(userActions.getAccountsListFailure.match),
    tap(({payload: message}) => Toast({message, type: 'error'})),
    ignoreElements(),
  )

const getTokenEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.setTokens.match),
    switchMap(({payload}) =>
      from(AuthService.getTokens(payload)).pipe(
        mergeMap(({data}) => [
          userActions.setTokensSuccess({respData: data, reqPayload: payload}),
          userActions.setUser({token: payload.token, role: UserRoles.admin}),
        ]),
        catchError(() => concat(of(userActions.setTokensFailure()))),
      ),
    ),
  )

const getTokenSuccessEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.setTokensSuccess.match),
    tap(({payload: {reqPayload, respData}}) => {
      if (respData.emailId && isFullStoryInitialized()) {
        FullStory('setProperties', {
          type: 'user',
          properties: {
            displayName: respData.emailId,
          },
        })
      }
      if (reqPayload.pageRefresh) {
        window.location.replace('/analytics/overview')
      }
    }),
    ignoreElements(),
  )

const setTokensFailureEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.setTokensFailure.match),
    tap(() => {
      Toast({
        message: 'Your session has expired. Please log in again.',
        type: 'error',
      })
      store.dispatch({type: 'reset/store'})
      history.push('/')
    }),
    ignoreElements(),
  )

const downloadInstallerEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(userActions.downloadInstaller.match),
    switchMap(({payload}) => {
      const installerLabel = getInstallerLabel(payload.bundle)
      return from(AuthService.downloadInstaller(payload)).pipe(
        concatMap(({data}) => {
          if (data?.fileUrl) {
            window.open(data.fileUrl, '_self')
          } else {
            Toast({
              message: t('common:DOWNLOAD_INSTALLER_FAILED', {
                label: installerLabel,
              }),
              type: 'error',
            })
          }
          return of(data).pipe(
            mergeMap(() => [userActions.downloadInstallerSuccess(data)]),
          )
        }),
        catchError(error => {
          const status = error?.status
          if (status === 400 && payload.bundle === InstallerType.WindowsArm) {
            Toast({
              message: t('common:WINDOWS_ARM_VERSION_UNSUPPORTED'),
              type: 'error',
            })
          } else {
            Toast({
              message: t('common:DOWNLOAD_INSTALLER_FAILED', {
                label: installerLabel,
              }),
              type: 'error',
            })
          }
          return concat(of(userActions.downloadInstallerFailure()))
        }),
      )
    }),
  )

const downloadInstallerFailureEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(userActions.downloadInstallerFailure.match),
    tap(({payload: message}) => {
      if (message) {
        Toast({message: 'Failed to download a file', type: 'error'})
      }
    }),
    ignoreElements(),
  )

const storeLocationEpic: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.storageLocation.match),
    switchMap(({payload}) =>
      from(AuthService.storeLocation(payload)).pipe(
        mergeMap(() => [userActions.storageLocationSuccess()]),
        catchError((message: string) =>
          concat(
            of(userActions.storageLocationSuccess()),
            of(userActions.setAsyncError({filter: 'user', message})),
          ),
        ),
      ),
    ),
  )
const updateCurrentStep: Epic<AppActions, AppActions, RootState> = action$ =>
  action$.pipe(
    filter(userActions.updateCurrentStep.match),
    tap(({payload}) => localStorage.setItem('currentStep', payload.toString())),
    ignoreElements(),
  )
export const userEpics = [
  getTokenEpic,
  logoutEpic,
  downloadInstallerEpic,
  downloadInstallerFailureEpic,
  getAccountsListEpic,
  getAccountsListFailureEpic,
  getTokenSuccessEpic,
  logoutFailureEpic,
  storeLocationEpic,
  updateCurrentStep,
  setTokensFailureEpic,
]
